import { SearchOutlined } from '@vindr/icons';
import { useGetDevices } from '@vindr/services/actions/devices.action';
import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { useCreateOrderGroup } from '@vindr/services/actions/order.action';
import { useGetPatientsVisited } from '@vindr/services/actions/patient.action';
import { ParamsCreateOrderGroup } from '@vindr/services/apis/order.api';
import { DeviceTypes, GENDER } from '@vindr/services/constants/common.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { convertPhoneNumber } from '@vindr/utils/formatText/formatText';
import { Pick, getDateFormatted, uuidv4 } from '@vindr/utils/helpers/common.helper';
import { useDebounce } from '@vindr/utils/hooks/useDebounce';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Select, Typography } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GENDER_OPTIONS } from '../../../Templates/Select';
import Table from '../../../ui/Table';
import FormItem from '../../FormItem/FormItem';
import PatientSearch from './patient/PatientSearch';
import ProcedureSearch from './procedure/ProcedureSearch';
import useProcedureColumns from './useProcedureColumns';
const { Title } = Typography;

export interface CreateOrderForm {
  institutionId?: string | null;
  onFinish: (orderIds: string[]) => void;
  onCancel: () => void;
  defaultValues?: {
    patient_name: string;
    birth_date: string;
    sex: GENDER;
    patient_id: string;
    device_id: string;
    visit_id?: string;
    order_group?: string;
    refer_doctor_name?: string;
    order_doctor_name?: string;
    diagnostic?: string;
  };
  disablePatientSearch?: boolean;
  message: MessageInstance;
}

const CreateOrderForm = ({
  institutionId = null,
  onFinish,
  onCancel,
  defaultValues,
  disablePatientSearch = false,
  message,
}: CreateOrderForm) => {
  const { t } = useTranslation('Vindoc');
  const [popupProcedureSearch, setPopupProcedureSearch] = useState(false);
  const [popupPatientSearch, setPopupPatientSearch] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(defaultValues);
  // const [searchPatientField, setSearchPatientField] = useState('patient_id');
  const [patientFilter, setPatientFilter] = useState({});

  const [orderForm] = Form.useForm();
  const [patientForm] = Form.useForm();
  const [procedureList, setProcedureList] = useState([]);
  const [searchInstitution, onSearchInstitution] = useDebounce<string>();
  const { data: institutions } =
    useGetInstitutions({ offset: 0, limit: 100, name: searchInstitution })?.data || {};
  const institution_id = Form.useWatch('institution_id', orderForm);

  const [searchDevice, onSearchDevice] = useDebounce<string>();
  const { data: devices } =
    useGetDevices({
      offset: 0,
      limit: 100,
      institution_ids: [institution_id],
      active: true,
      types: [DeviceTypes.Modality],
      name: searchDevice,
    })?.data || {};
  const procedureColumns = useProcedureColumns({
    onRemove: (record) =>
      setProcedureList(
        procedureList.filter((it) => it?.id != record.id || it?.timestamp != record?.timestamp)
      ),
  });
  const { mutate: createOrderGroup } = useCreateOrderGroup();

  // ? Study and selected Order not have order id, so we need call api getPatientsVisited and get order id
  const { data: patient } =
    useGetPatientsVisited(
      {
        offset: 0,
        limit: 100,
        visit_id: defaultValues?.visit_id,
        patient_name: defaultValues?.patient_name,
        patient_id: defaultValues?.patient_id,
      },
      { enabled: !!defaultValues?.visit_id || !!defaultValues?.patient_id }
    )?.data || {};

  useEffect(() => {
    if (!patient || !defaultValues) return;

    // ? Have more than patient when same visit_id. Example: PVI23040008 and PVI23040008abc
    const patientId =
      patient.length > 1
        ? patient.find((it) => it?.visit_id === defaultValues?.visit_id)?.id
        : patient?.[0]?.id;

    setSelectedPatient((prev) => ({ ...prev, id: patientId }));
  }, [patient, defaultValues]);

  useEffect(() => {
    if (!defaultValues) return;

    const orderDefault = Pick(defaultValues, [
      'device_id',
      'refer_doctor_name',
      'order_doctor_name',
      'diagnostic',
    ] satisfies (keyof typeof defaultValues)[]);

    Object.entries(orderDefault).forEach(([key, value]) => {
      orderForm.setFieldValue(key, value);
    });
  }, [defaultValues, orderForm]);

  useEffect(() => {
    patientForm.setFieldsValue({
      ...selectedPatient,
      birth_date: selectedPatient?.birth_date ? dayjs(selectedPatient?.birth_date) : null,
    });
  }, [patientForm, selectedPatient]);

  useEffect(() => {
    if (institution_id) {
      setPatientFilter({
        institution_ids: [institution_id],
      });
    }
  }, [institution_id]);

  useEffect(() => {
    if (!devices || !devices.length) return;
    const deviceId = orderForm.getFieldValue('device_id');

    if (!devices.find((it) => it?.id == deviceId)) {
      orderForm.setFieldValue('device_id', null);
    }
  }, [devices, orderForm]);

  const handleSave = () => {
    const procedure_ids = procedureList.map((it) => it.id);

    if (!procedure_ids.length) {
      message.error(t(MESSAGE.ERROR.EMSG_8018));

      return;
    }

    Promise.all([patientForm.validateFields(), orderForm.validateFields()])
      .then(([patient, order]) => {
        const input: ParamsCreateOrderGroup = {
          ...order,
          procedure_ids,
          visited_patient: {
            ...selectedPatient,
            ...patient,
            phone: convertPhoneNumber(patient?.phone || selectedPatient?.phone),
          },
        };

        createOrderGroup(input, {
          onSuccess: (data) => {
            const orderIds = data?.data?.order_ids;

            message.success(t(MESSAGE.SUCCESS.SCD_8000));
            onFinish(orderIds);
          },
          onError: (error: any) => {
            const error_message =
              (error?.response?.data?.error_code &&
                MESSAGE.ERROR[error?.response?.data?.error_code]) ||
              'Create error';

            message.error(t(error_message));
          },
        });
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  };

  useEffect(() => {
    orderForm.setFieldValue('institution_id', institutionId || institutions?.[0]?.id);
  }, []);

  return (
    <div>
      <Row gutter={[36, 36]}>
        <Col xs={24} xl={12}>
          <Row className="mb-5">
            <Col span={12} className="mb-0 flex items-center">
              <Title level={3} className="mb-0">
                {t('Patient Information')}
              </Title>
            </Col>
            <Col span={12}>
              <div className="flex justify-end">
                <Button
                  className="flex items-center px-2"
                  onClick={() => setPopupPatientSearch(true)}
                  disabled={disablePatientSearch}
                >
                  {t('Patient Search')}
                  <SearchOutlined className="fill-[#000] dark:fill-[#fff]" />
                </Button>
              </div>
            </Col>
          </Row>
          <div>
            <Form layout="vertical" form={orderForm} name="form" onFinish={handleSave}>
              <FormItem.InstitutionList
                name="institution_id"
                wrapperClassName="mb-2 w-full"
                required
                placeholder={t('Select Institution')}
                disabled={!!selectedPatient || !!institutionId}
                multiple={false}
                onChange={() => {
                  patientForm.resetFields();
                  setSelectedPatient(null);
                }}
                allowClear={false}
                showSearch
                onSearch={onSearchInstitution}
              />
            </Form>
            <Form
              layout="vertical"
              form={patientForm}
              name="form"
              onFinish={handleSave}
              className="form-filter-condition"
            >
              <FormItem.Input
                name="patient_name"
                label={t('Patient Name')}
                placeholder={t('Enter Patient Name')}
                className="mb-2"
                required
              />
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <FormItem.Birthday name="birth_date" className="mb-2" required />
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="sex"
                    label={t('Gender')}
                    className="mb-2"
                    required
                    rules={[{ required: true, message: t('Required value') }]}
                    initialValue={GENDER.Other}
                  >
                    <Select
                      placeholder={t('Select')}
                      options={GENDER_OPTIONS.map((gender) => ({
                        ...gender,
                        label: t(gender.label),
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="patient_id" label={t('Patient ID')} className="mb-2">
                    <Input placeholder={t('Enter Patient ID')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="visit_id" label={t('Visit ID')} className="mb-2">
                    <Input
                      placeholder={t('Enter Visit ID')}
                      disabled={!!defaultValues?.visit_id || !!selectedPatient?.visit_id}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12} className="w-full">
                  <FormItem.PhoneNumber className="mb-2" />
                </Col>
                <Col xs={24} sm={12}>
                  <FormItem.Email className="mb-2" />
                </Col>
              </Row>
              <FormItem.InputArea
                label={t('Address')}
                placeholder={t('Enter Address')}
                name="address"
                formClassName="mb-0"
                className="min-h-[166px]"
              />
            </Form>
          </div>
        </Col>
        <Col xs={24} xl={12} className="flex flex-col">
          <Title level={3} className="mb-6">
            {t('Order Information')}
          </Title>
          <Form layout="vertical" form={orderForm} name="form" onFinish={handleSave}>
            <Form.Item
              name="scheduled_date"
              label={t('Scheduled Date')}
              className="mb-2"
              required
              rules={[{ required: true, message: t('Required value') }]}
              initialValue={dayjs()}
            >
              <DatePicker className="w-full" showTime format={getDateFormatted(true)} />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <FormItem.Select
                  label={t('Device')}
                  name="device_id"
                  placeholder={t('Select Device')}
                  wrapperClassName="mb-2"
                  className="mb-0"
                  required
                  searchVietnamese
                  showSearch
                  options={devices?.map((it) => ({ label: it?.name, value: it?.id }))}
                  onSearch={onSearchDevice}
                  onClear={() => {
                    onSearchDevice(null);
                  }}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="order_group" label={t('Order Group')} className="mb-2">
                  <Input placeholder={t('Enter Order Group')} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="refer_doctor_name" label={t('Refer Doctor')} className="mb-2">
                  <Input placeholder={t('Enter Refer Doctor')} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="order_doctor_name" label={t('Order Doctor')} className="mb-2">
                  <Input placeholder={t('Enter Order Doctor')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="diagnostic" label={t('Diagnoses')} className="mb-9">
              <Input placeholder={t('Enter Diagnoses')} />
            </Form.Item>
          </Form>
          <Table
            columns={procedureColumns}
            dataSource={procedureList || []}
            style={{ background: '#2c5166', height: '100%' }}
            pagination={null}
            wrapperClassName="procedure-table grow-1 shrink basis-[auto] min-h-[202px]"
            rowKey={() => uuidv4()}
          />
          <Button className="w-full" onClick={() => setPopupProcedureSearch(true)}>
            {t('Add Procedure')}
          </Button>
          <Modal
            className="vindr-modal patient-search max-w-screen"
            title={t('Patient Search')}
            open={popupPatientSearch}
            maskClosable={false}
            destroyOnClose
            zIndex={1001}
            onCancel={() => setPopupPatientSearch(false)}
            footer={null}
            width="90%"
          >
            <PatientSearch
              initFilters={patientFilter}
              onSave={(patient) => {
                setSelectedPatient(patient);
                setPopupPatientSearch(false);
              }}
              onCancel={() => setPopupPatientSearch(false)}
            />
          </Modal>
          <Modal
            className="vindr-modal procedure-search max-w-screen w-[900px]"
            title={t('Procedure Search')}
            open={popupProcedureSearch}
            maskClosable={false}
            destroyOnClose
            zIndex={1001}
            onCancel={() => setPopupProcedureSearch(false)}
            footer={null}
          >
            <ProcedureSearch
              institution_id={institution_id}
              onCancel={() => setPopupProcedureSearch(false)}
              onSave={(procedures) => {
                setProcedureList([...procedureList, ...procedures]);
                setPopupProcedureSearch(false);
              }}
            />
          </Modal>
        </Col>
      </Row>
      <div className="footer mt-3 flex justify-end">
        <Button onClick={onCancel} className="mr-1">
          {t('Cancel')}
        </Button>
        <Button type="primary" onClick={handleSave}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default memo(CreateOrderForm, (prevProps, nextProps) => {
  return isEqual(prevProps.defaultValues, nextProps.defaultValues);
});
